import { z } from 'zod';
import { brandingSchema } from './domains.js';
import { fieldOf, isUnknownValue, knownEntries, knownOr, knownValues } from './forward-compatible.js';
import { ACCESS_CONTEXT_TYPES, PRODUCT_KEYS, productKeySchema } from './products.js';
import { PORTAL_ROLES, STAFF_ROLES, orgRoleSchema, platformPermissionSchema, portalRoleSchema, staffRoleSchema, } from './roles.js';
/**
 * GET /v1/me/access: the authoritative answer to "where may this signed-in person go?".
 * It is resolved on every call from platform records (memberships, subscriptions, entitlements,
 * portal grants, staff grants). Clients never infer access from UI choices, storage, email suffixes
 * or profile metadata, and a failed resolution never falls back to "everything".
 *
 * Product states: available (open it), not_entitled, unavailable (not deployed here), suspended, and
 * sso_required: the person may use the product, but not with this session, because the organization
 * enforces its own sign-in (SSO connection) and this session came through another one. Signing in again
 * through the organization (the context's sso hints) makes it available; navigation/decide answers
 * reauthenticate for it. A product or API authorizing from me/access must treat only available as open.
 *
 * Host scope: when the request Origin is a verified customer host, the response lists only the contexts
 * that host serves (that organization's employee context on an employee-app host, that portal's contexts
 * on a customer-portal host), the same narrowing navigation/decide applies. On Trace hosts, and for calls
 * without a browser Origin, it lists every context.
 */
export const PRODUCT_ACCESS_STATES = [
    'available',
    'not_entitled',
    'unavailable',
    'suspended',
    'sso_required',
];
export const organizationSummarySchema = z.object({
    id: z.uuid(),
    slug: z.string(),
    displayName: z.string(),
    status: z.enum(['active', 'suspended']),
    branding: brandingSchema,
});
export const productAccessSchema = z.object({
    productKey: productKeySchema,
    state: z.enum(PRODUCT_ACCESS_STATES),
    reason: z.string().optional(),
    /** Registered destination for this environment; absent when the product is not deployed here. */
    destination: z.object({ origin: z.string(), defaultPath: z.string() }).optional(),
});
/**
 * 0.22.0: the products of a context, read forward compatibly (forward-compatible.ts): an entry naming a product or a
 * state this copy does not know is dropped, so a product platform-api adds later never breaks an older reader. A
 * reader then simply does not see that product, which opens nothing.
 */
const productAccessListSchema = knownEntries(productAccessSchema, (raw) => isUnknownValue(fieldOf(raw, 'productKey'), PRODUCT_KEYS) ||
    isUnknownValue(fieldOf(raw, 'state'), PRODUCT_ACCESS_STATES));
/**
 * The organization's sign-in requirement as it applies to this session. Present when the organization
 * has an SSO policy. enforced: only its connection may be used; satisfied: this session came through it
 * (always true when not enforced). The hints are opaque identity-provider references (an Auth0
 * organization id and a connection name) for restarting sign-in, not secrets.
 */
export const employeeSsoSchema = z.object({
    enforced: z.boolean(),
    satisfied: z.boolean(),
    organizationHint: z.string().optional(),
    connectionHint: z.string().optional(),
});
/**
 * 0.8.0: whether the person administers this organization, and with which permissions, so the account hub shows
 * the organization settings only where they are allowed. Present only for an active membership of an active
 * organization whose roles hold an administrative permission (ORGANIZATION_ADMINISTRATION_PERMISSIONS); absent for
 * plain members, suspended memberships and suspended organizations. state is available when the organization
 * administration API (/v1/organizations/{orgId}/...) answers this session, and sso_required when the organization
 * enforces its own sign-in and this session came through another connection (sign in again through the context's
 * sso hints first). A display hint only: the server decides every request from the database at that moment.
 */
export const ORGANIZATION_ADMINISTRATION_STATES = ['available', 'sso_required'];
export const employeeAdministrationSchema = z.object({
    state: z.enum(ORGANIZATION_ADMINISTRATION_STATES),
    /**
     * The organization permissions of the membership's roles (ORG_ROLE_PERMISSIONS), in contract order. 0.22.0: a
     * permission this copy does not know is dropped (contracts 0.18.0 added tickets.*, which older copies refused).
     */
    permissions: knownValues(platformPermissionSchema),
});
export const employeeContextSchema = z.object({
    id: z.string(),
    type: z.literal('employee'),
    organization: organizationSummarySchema,
    /** 0.22.0: a role this copy does not know is dropped (contracts 0.18.0 added help_desk). */
    roles: knownValues(orgRoleSchema),
    products: productAccessListSchema,
    sso: employeeSsoSchema.optional(),
    /**
     * The identity-provider organization of this organization (an Auth0 organization id), whenever it has one,
     * with or without an SSO policy: what an Auth0 sign-in into this workspace sends as its organization. An
     * opaque reference, not a secret. Products that require an Auth0 organization (auth/tenant) cannot sign a
     * person in to a workspace without it.
     */
    organizationHint: z.string().optional(),
    /**
     * 0.8.0: present where the person administers the organization (employeeAdministrationSchema). 0.22.0: in a state
     * this copy does not know it reads as absent (no administration shown), never as a failed answer.
     */
    administration: knownOr(employeeAdministrationSchema.optional(), (raw) => isUnknownValue(fieldOf(raw, 'state'), ORGANIZATION_ADMINISTRATION_STATES), undefined),
});
export const portalContextSchema = z.object({
    id: z.string(),
    type: z.literal('portal'),
    providerOrganization: organizationSummarySchema,
    portal: z.object({ id: z.uuid(), displayName: z.string(), productKey: productKeySchema }),
    customerAccount: z.object({
        id: z.uuid(),
        displayName: z.string(),
        /**
         * The provider's own reference for this customer (Trace Admin's "Reference", for example its code in the
         * provider's TMS), when set. The Customer Portal links an account to the provider's customer record by it.
         */
        reference: z.string().optional(),
    }),
    role: portalRoleSchema,
    products: productAccessListSchema,
});
export const staffContextSchema = z.object({
    id: z.literal('staff'),
    type: z.literal('staff'),
    staffRole: staffRoleSchema,
    mfaSatisfied: z.boolean(),
    products: productAccessListSchema,
});
export const accessContextSchema = z.discriminatedUnion('type', [
    employeeContextSchema,
    portalContextSchema,
    staffContextSchema,
]);
/**
 * 0.22.0: true for a context, in a list of an answer, that this copy cannot read: a kind outside kinds (a kind of
 * context a later version adds), a portal context with a portal role or product this copy does not know, or a staff
 * context with a staff role it does not know. Lists of contexts drop such an entry (forward-compatible.ts), which
 * opens nothing: the person simply does not see that context until the reader is updated.
 */
export function isUnknownContext(raw, kinds = ACCESS_CONTEXT_TYPES) {
    const type = fieldOf(raw, 'type');
    if (isUnknownValue(type, kinds))
        return true;
    if (type === 'portal') {
        return (isUnknownValue(fieldOf(raw, 'role'), PORTAL_ROLES) ||
            isUnknownValue(fieldOf(fieldOf(raw, 'portal'), 'productKey'), PRODUCT_KEYS));
    }
    if (type === 'staff')
        return isUnknownValue(fieldOf(raw, 'staffRole'), STAFF_ROLES);
    return false;
}
export const meAccessSchema = z.object({
    subject: z.object({
        userId: z.uuid(),
        displayName: z.string(),
        /** For display only. Email is never an identity key. */
        email: z.string().optional(),
    }),
    identity: z.object({
        mode: z.enum(['auth0', 'development']),
        issuer: z.string(),
        provider: z.string().optional(),
    }),
    /** 0.22.0: a context this copy cannot read (isUnknownContext) is dropped. */
    contexts: knownEntries(accessContextSchema, (raw) => isUnknownContext(raw)),
    generatedAt: z.string(),
    /** Clients may cache this response at most this long; changes in membership apply after it. */
    maxAgeSeconds: z.number().int().nonnegative(),
});
/** Context ids are opaque to clients; these builders keep the server's format in one place. */
export const contextIds = {
    employee: (organizationId) => `employee:${organizationId}`,
    portal: (portalId, customerAccountId) => `portal:${portalId}:${customerAccountId}`,
    staff: () => 'staff',
};
//# sourceMappingURL=access.js.map